// Onglet « Décision » de la candidature côté bailleur (F-029) : décider avec motifs, historique, contestations.
import { useState } from "react";
import { post } from "@core/api";
import { useGet } from "@core/hooks";
import { dateTime } from "@core/format";
import { useOrg } from "@core/org";
import { Button, Card, Checkbox, Empty, ErrorBox, Loading, Notice, StatusChip, Table, Textarea, fieldError, type CheckStatus } from "@core/ui";
import { APPLICATION_STATUS, ChoiceGroup, OUTCOME_LABELS, OutcomeChip, useAction } from "./shared";

type Reason = { code: string; label: string; outcomes: string[]; source: "catalogue" | "campagne" };
type DecisionRow = {
  id: string;
  outcome: string;
  reasons: { code: string; label: string; source: string }[];
  note: string;
  previous_status: string;
  decided_by_name: string | null;
  decided_at: string;
  candidates_notified: number;
  superseded_at: string | null;
  contestation_id: string | null;
};
export type Contestation = {
  id: string;
  application_id: string;
  author_name: string | null;
  kind: "explication" | "contestation";
  message: string;
  status: "ouverte" | "repondue";
  response: string | null;
  responded_by_name: string | null;
  responded_at: string | null;
  decision_changed: boolean;
  created_at: string;
  household_label: string;
  campaign_title: string;
  decision_outcome: string | null;
};
type View = {
  application: { id: string; status: string; household_label: string; campaign_title: string };
  current: DecisionRow | null;
  history: DecisionRow[];
  reasons: Reason[];
  contestations: Contestation[];
  pending: { open_corrections: number; open_contestations: number; checks_by_status: Record<string, number>; active_checks: number };
  journal: { action: string; user_name: string | null; created_at: string; details: any }[];
  can_decide: boolean;
};

const OUTCOMES: { value: "retenue" | "refusee" | "liste_attente"; label: string; help: string; cta: string }[] = [
  { value: "retenue", label: "Retenir ce dossier", help: "Vous préparerez ensuite le bail.", cta: "Retenir ce dossier" },
  { value: "liste_attente", label: "Placer en liste d’attente", help: "La candidature reste active.", cta: "Placer en liste d’attente" },
  { value: "refusee", label: "Ne pas retenir ce dossier", help: "Un motif admissible est obligatoire.", cta: "Ne pas retenir ce dossier" },
];

const STATUS_ORDER: CheckStatus[] = ["conforme", "a_confirmer", "anomalie", "manquant", "non_concluant", "info"];

const JOURNAL_LABELS: Record<string, string> = {
  "candidature.decision": "Décision enregistrée",
  "candidature.contestation_recue": "Demande du candidat reçue",
  "candidature.contestation_repondue": "Réponse apportée au candidat",
  "correction.demandee": "Correction demandée",
  "correction.resolue": "Correction résolue",
  "correction.annulee": "Correction annulée",
  "correction.piece_deposee": "Pièce déposée en réponse",
  "correction.reponse_candidat": "Réponse écrite du candidat",
  "confirmation_employeur.preparee": "Confirmation employeur préparée",
  "confirmation_employeur.consentement": "Accord du candidat enregistré",
  "confirmation_employeur.envoyee": "Demande envoyée à l’employeur",
  "confirmation_employeur.relancee": "Employeur relancé",
  "confirmation_employeur.reponse": "Réponse de l’employeur",
  "confirmation_employeur.expiree": "Demande à l’employeur expirée",
  "confirmation_employeur.annulee": "Demande à l’employeur annulée",
  "mes_droits_sociaux.demandee": "Attestation Mes droits sociaux proposée",
  "mes_droits_sociaux.deposee": "Attestation Mes droits sociaux déposée",
  "mes_droits_sociaux.verifiee": "Attestation Mes droits sociaux vérifiée",
  "identite.demandee": "Justificatif d’identité proposé",
  "identite.deposee": "Justificatif d’identité déposé",
  "identite.verifiee": "Identité examinée",
  "garant.acte_prepare": "Acte de cautionnement préparé",
  "garant.invite": "Garant invité",
  "garant.engagement_recueilli": "Engagement du garant recueilli",
  "garant.acte_signe": "Acte de cautionnement signé",
  "garant.engagement_annule": "Engagement annulé",
  "visale.declare": "Visa Visale déclaré",
  "visale.verifie": "Visa Visale vérifié",
  "visale.contrat_signe": "Contrat de cautionnement Visale",
  "visale.active": "Garantie Visale active",
  "visale.annule": "Visa Visale annulé",
  "gli.evaluation_enregistree": "Évaluation GLI enregistrée",
  "gli.souscription_suivie": "Suivi de souscription GLI",
  "candidature.importee_dossierfacile": "Import DossierFacile",
  "dossierfacile.statut_recu": "Statut DossierFacile reçu",
};

export function DecisionTab({ orgId, applicationId }: { orgId: string; applicationId: string }) {
  const org = useOrg();
  const base = `/api/v1/orgs/${orgId}/applications/${applicationId}`;
  const q = useGet<View>(`${base}/decision`);
  const action = useAction([base, `/api/v1/orgs/${orgId}/contestations`, `/api/v1/orgs/${orgId}/decision-summary`]);
  const [outcome, setOutcome] = useState<"" | "retenue" | "refusee" | "liste_attente">("");
  const [reasons, setReasons] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const [notify, setNotify] = useState(true);
  const [contestationId, setContestationId] = useState("");

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const v = q.data!;
  const available = v.reasons.filter((r) => outcome && r.outcomes.includes(outcome));
  const chosen = OUTCOMES.find((o) => o.value === outcome);
  const canDecide = v.can_decide && org.can("decide");
  const counts = STATUS_ORDER.filter((s) => v.pending.checks_by_status[s]);

  return (
    <div className="stack">
      <Card title="Avant de décider">
        <p className="muted">
          Bailivo ne classe pas les candidatures et ne décide pas à votre place. Examinez les constats et leurs sources, puis décidez. Statut actuel de la candidature :{" "}
          <strong>{APPLICATION_STATUS[v.application.status] ?? v.application.status}</strong>.
        </p>
        {counts.length ? (
          <ul className="list-plain decision-compteurs" aria-label="Constats actifs par statut">
            {counts.map((s) => (
              <li key={s}>
                <StatusChip status={s} /> <span className="mono">{v.pending.checks_by_status[s]}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="small muted">Aucun constat enregistré pour ce dossier.</p>
        )}
        {v.pending.open_corrections > 0 ? (
          <Notice tone="attention">
            {v.pending.open_corrections} demande{v.pending.open_corrections > 1 ? "s" : ""} de correction en attente de réponse du candidat.
          </Notice>
        ) : null}
        {v.pending.open_contestations > 0 ? <Notice tone="attention">{v.pending.open_contestations} demande(s) du candidat attendent votre réponse (voir plus bas).</Notice> : null}
      </Card>

      <Card title="Décision actuelle">
        {v.current ? (
          <div className="stack-sm">
            <p className="row">
              <OutcomeChip outcome={v.current.outcome} />
              <span className="small muted">
                le {dateTime(v.current.decided_at)} par {v.current.decided_by_name ?? "un membre de l’espace"}
              </span>
            </p>
            {v.current.reasons.length ? (
              <ul>
                {v.current.reasons.map((r) => (
                  <li key={r.code}>{r.label}</li>
                ))}
              </ul>
            ) : null}
            {v.current.note ? (
              <p className="small">
                <span className="muted">Note interne (non communiquée au candidat) :</span> {v.current.note}
              </p>
            ) : null}
            <p className="small muted">{v.current.candidates_notified ? `Candidat informé par e-mail (${v.current.candidates_notified} envoi(s) simulé(s)).` : "Candidat non informé par e-mail."}</p>
          </div>
        ) : (
          <Empty title="Aucune décision pour ce dossier">Vous décidez quand vous le souhaitez, après examen des pièces et constats.</Empty>
        )}
      </Card>

      {canDecide ? (
        <Card title={v.current ? "Modifier la décision" : "Décider"}>
          <form
            className="stack"
            onSubmit={async (e) => {
              e.preventDefault();
              if (!outcome) return;
              const ok = await action.run(
                "decider",
                () => post(`${base}/decisions`, { outcome, reasons, note, notify_candidate: notify, contestation_id: contestationId || null }),
                `Décision enregistrée : ${OUTCOME_LABELS[outcome]}.`,
              );
              if (ok) {
                setOutcome("");
                setReasons([]);
                setNote("");
                setContestationId("");
              }
            }}
          >
            <ChoiceGroup
              legend="Votre décision"
              name="decision-issue"
              value={outcome}
              onChange={(o) => {
                setOutcome(o);
                setReasons((rs) => rs.filter((code) => v.reasons.find((r) => r.code === code)?.outcomes.includes(o)));
              }}
              options={OUTCOMES.map((o) => ({ value: o.value, label: o.label, help: o.help }))}
            />
            {outcome ? (
              <fieldset className="decision-choix">
                <legend>Motifs {outcome === "retenue" ? "(facultatifs)" : "(au moins un)"}</legend>
                <p className="small muted">Seuls des motifs factuels et admissibles sont proposés : ceux du catalogue et les critères que vous avez fixés pour cette campagne.</p>
                {available.map((r) => (
                  <Checkbox
                    key={r.code}
                    label={r.label}
                    help={r.source === "campagne" ? "Critère de la campagne" : undefined}
                    checked={reasons.includes(r.code)}
                    onChange={(c) => setReasons((rs) => (c ? [...rs, r.code] : rs.filter((x) => x !== r.code)))}
                  />
                ))}
                {fieldError(action.error, "reasons") ? (
                  <p className="bv-champ-aide decision-erreur" role="alert">
                    {fieldError(action.error, "reasons")}
                  </p>
                ) : null}
              </fieldset>
            ) : null}
            <Textarea
              label={v.current ? "Pourquoi modifiez-vous la décision ?" : "Note interne (facultative)"}
              help="Cette note reste dans le journal de l’espace ; elle n’est pas envoyée au candidat."
              value={note}
              required={!!v.current}
              error={fieldError(action.error, "note")}
              onChange={(e) => setNote(e.target.value)}
            />
            {v.contestations.some((c) => c.status === "repondue" || c.status === "ouverte") ? (
              <label className="bv-champ">
                <span>Décision prise à la suite d’une demande du candidat (facultatif)</span>
                <select value={contestationId} onChange={(e) => setContestationId(e.target.value)}>
                  <option value="">Aucune</option>
                  {v.contestations.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.kind === "contestation" ? "Contestation" : "Demande d’explication"} du {dateTime(c.created_at)}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <Checkbox label="Informer le candidat par e-mail" help="Le message indique la décision et les motifs, et rappelle qu’il peut demander des explications ou contester." checked={notify} onChange={setNotify} />
            <div className="form-actions">
              <Button type="submit" busy={action.busy === "decider"} disabled={!outcome}>
                {chosen?.cta ?? "Choisissez une décision"}
              </Button>
            </div>
            {action.error && !fieldError(action.error, "reasons") && !fieldError(action.error, "note") ? <ErrorBox error={action.error} /> : null}
          </form>
        </Card>
      ) : (
        <Notice>Votre rôle permet de consulter ce dossier. La décision revient au propriétaire ou au gestionnaire de l’espace.</Notice>
      )}

      <Card title="Demandes d’explication et contestations">
        {v.contestations.length ? (
          <div className="stack">
            {v.contestations.map((c) => (
              <ContestationItem key={c.id} c={c} orgId={orgId} canRespond={canDecide} prefixes={[base, `/api/v1/orgs/${orgId}/contestations`, `/api/v1/orgs/${orgId}/decision-summary`]} />
            ))}
          </div>
        ) : (
          <p className="muted">Le candidat n’a formulé aucune demande.</p>
        )}
      </Card>

      <Card title="Historique des décisions">
        {v.history.length ? (
          <Table caption="Historique des décisions">
            <thead>
              <tr>
                <th>Date</th>
                <th>Décision</th>
                <th>Motifs</th>
                <th>Par</th>
                <th>État</th>
              </tr>
            </thead>
            <tbody>
              {v.history.map((d) => (
                <tr key={d.id}>
                  <td>{dateTime(d.decided_at)}</td>
                  <td>
                    <OutcomeChip outcome={d.outcome} />
                  </td>
                  <td>{d.reasons.map((r) => r.label).join(" ; ") || "—"}</td>
                  <td>{d.decided_by_name ?? "—"}</td>
                  <td>{d.superseded_at ? `Remplacée le ${dateTime(d.superseded_at)}` : "En vigueur"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="muted">Aucune décision enregistrée.</p>
        )}
      </Card>

      <Card title="Journal du dossier">
        {v.journal.length ? (
          <ol className="list-plain decision-journal">
            {v.journal.map((j, i) => (
              <li key={i}>
                <span className="mono small">{dateTime(j.created_at)}</span> <strong>{JOURNAL_LABELS[j.action] ?? j.action}</strong>
                <span className="muted small"> · {j.user_name ?? "Candidat, employeur ou système"}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="muted">Aucune action enregistrée pour l’instant.</p>
        )}
      </Card>
    </div>
  );
}

export function ContestationItem({ c, orgId, canRespond, prefixes, showContext }: { c: Contestation; orgId: string; canRespond: boolean; prefixes: string[]; showContext?: boolean }) {
  const [response, setResponse] = useState("");
  const action = useAction(prefixes);
  return (
    <article className="decision-echange">
      <header className="row-between">
        <div>
          <strong>{c.kind === "contestation" ? "Contestation" : "Demande d’explication"}</strong>
          <span className="small muted">
            {" "}
            de {c.author_name ?? "le candidat"}, le {dateTime(c.created_at)}
            {showContext ? ` · ${c.household_label || "Candidature"} · ${c.campaign_title}` : ""}
          </span>
        </div>
        <StatusChip status={c.status === "ouverte" ? "a_confirmer" : "conforme"} label={c.status === "ouverte" ? "En attente de votre réponse" : "Répondu"} />
      </header>
      {c.decision_outcome ? <p className="small muted">Décision concernée : {OUTCOME_LABELS[c.decision_outcome] ?? c.decision_outcome}</p> : null}
      <p className="message-body">{c.message}</p>
      {c.response ? (
        <div className="decision-reponse">
          <p className="small muted">
            Réponse de {c.responded_by_name ?? "l’espace"} le {dateTime(c.responded_at)}
            {c.decision_changed ? " · la décision a été modifiée" : ""}
          </p>
          <p className="message-body">{c.response}</p>
        </div>
      ) : canRespond ? (
        <form
          className="stack-sm"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await action.run("repondre", () => post(`/api/v1/orgs/${orgId}/contestations/${c.id}/respond`, { response }), "Réponse envoyée au candidat.");
            if (ok) setResponse("");
          }}
        >
          <Textarea
            label="Votre réponse au candidat"
            help="Répondez sur des faits. Si une erreur est reconnue, modifiez la décision dans l’onglet Décision de la candidature."
            value={response}
            error={fieldError(action.error, "response")}
            onChange={(e) => setResponse(e.target.value)}
          />
          <div className="form-actions">
            <Button type="submit" small busy={action.busy === "repondre"}>
              Envoyer la réponse
            </Button>
          </div>
        </form>
      ) : null}
    </article>
  );
}
